import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import {
  ShieldAlert, Shield, ArrowLeft, Loader2, MapPin, Camera, CheckCircle2, XCircle,
  RefreshCw, Clock, AlertTriangle, ExternalLink, Zap, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime12h } from '../../utils/timeFormat';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN = () => localStorage.getItem('token');

/**
 * Iter-164 Phase 2 — Attendance Review UX polish.
 *
 *   • Filter chips with per-bucket counters (instead of a select)
 *   • Color-coded status badges (outside_radius_review = amber,
 *     gps_only_review = orange, selfie_failed = rose, pending = slate)
 *   • Selfie lightbox preview enlargement
 *   • Bulk approve (outside-radius only)
 *   • Mobile-first cards with larger tap targets
 *   • Distance vs allowed-radius shown as labelled chip
 */

const STATUS_META = {
  outside_radius_review: {
    label: 'Outside Radius',
    chip: 'bg-amber-100 text-amber-900 border-amber-200',
    chipActive: 'bg-amber-500 text-white border-amber-500',
    icon: MapPin,
    helper: 'Talent had GPS but was outside the allowed venue radius.',
    bulkable: true,
  },
  gps_only_review: {
    label: 'Selfie Failed (GPS OK)',
    chip: 'bg-orange-100 text-orange-900 border-orange-200',
    chipActive: 'bg-orange-500 text-white border-orange-500',
    icon: Camera,
    helper: 'GPS verified, but the selfie capture failed.',
    bulkable: false,
  },
  selfie_failed: {
    label: 'Selfie Failed (No GPS)',
    chip: 'bg-rose-100 text-rose-900 border-rose-200',
    chipActive: 'bg-rose-500 text-white border-rose-500',
    icon: Camera,
    helper: 'Selfie required but failed; no GPS captured either.',
    bulkable: false,
  },
  pending: {
    label: 'Pending Verification',
    chip: 'bg-slate-100 text-slate-800 border-slate-200',
    chipActive: 'bg-slate-700 text-white border-slate-700',
    icon: AlertTriangle,
    helper: 'Awaiting verification — record incomplete.',
    bulkable: false,
  },
  // Iter-213 P0 — new states for the inline admin quick-actions on
  // the Attendance & Compliance card.
  pending_correction: {
    label: 'Awaiting Selfie Correction',
    chip: 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200',
    chipActive: 'bg-fuchsia-500 text-white border-fuchsia-500',
    icon: Camera,
    helper: 'Admin asked the talent to retake their selfie. Waiting for upload.',
    bulkable: false,
  },
  approved_gps_only: {
    label: 'Approved (GPS-only)',
    chip: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    chipActive: 'bg-emerald-500 text-white border-emerald-500',
    icon: Shield,
    helper: 'Admin acknowledged a legitimate GPS-only check-in.',
    bulkable: false,
  },
  // Iter-411j — GPS fallback bucket. Talent was onsite but the browser
  // couldn't return a usable position, so they invoked "Check in
  // without GPS". Reason (permission_denied / timeout /
  // position_unavailable) is surfaced from activity_logs.
  gps_fallback_review: {
    label: 'GPS Fallback',
    chip: 'bg-cyan-100 text-cyan-900 border-cyan-200',
    chipActive: 'bg-cyan-500 text-white border-cyan-500',
    icon: MapPin,
    helper: 'Talent onsite but browser GPS failed — needs verification.',
    bulkable: false,
  },
  // Legacy aliases (read-only — backend normalizes)
  gps_warning:  { label: 'Outside Radius', chip: 'bg-amber-100 text-amber-900 border-amber-200', icon: MapPin, helper: '', bulkable: true },
  unverified:   { label: 'Pending', chip: 'bg-slate-100 text-slate-800 border-slate-200', icon: AlertTriangle, helper: '', bulkable: false },
};

const FAILURE_REASON = (r) => {
  const v = r.verification;
  if (v === 'outside_radius_review' || v === 'gps_warning') {
    const dist = r.distance_meters ? `${Math.round(r.distance_meters)}m` : '?';
    const radius = r?.shift?.radius_meters ? `${Math.round(r.shift.radius_meters)}m` : '?';
    return `Talent was ${dist} from venue (allowed: ${radius}). GPS captured.`;
  }
  if (v === 'gps_only_review') return 'Selfie capture failed but GPS verified within radius.';
  if (v === 'selfie_failed') return 'Selfie required but failed; no GPS captured.';
  if (v === 'gps_fallback_review') {
    const reasonLabels = {
      permission_denied:     'Talent denied or blocked location permission on their device.',
      position_unavailable:  'Device reported GPS is unavailable (likely indoors / weak signal).',
      timeout:               'Browser timed out trying to fix a GPS position.',
      unspecified:           'Talent used the GPS-fallback path — reason not captured.',
    };
    const r2 = r.gps_fallback_reason || 'unspecified';
    return reasonLabels[r2] || `GPS unavailable (${r2}).`;
  }
  if (v === 'pending' || v === 'unverified') return 'Awaiting verification — record incomplete.';
  return 'Manual review needed.';
};

const ClockInReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [breakdown, setBreakdown] = useState({
    outside_radius_review: 0, gps_only_review: 0, selfie_failed: 0,
    pending: 0, pending_correction: 0, approved_gps_only: 0,
    gps_fallback_review: 0,
  });
  // Iter-411j — GPS-fallback analytics banner state (inline, not a new page).
  const [fallbackStats, setFallbackStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | one of STATUS_META keys
  const [selected, setSelected] = useState(new Set()); // booking_ids
  const [actionDialog, setActionDialog] = useState(null); // {mode, review} OR {mode:'bulk'}
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const totalPending = useMemo(
    () => Object.values(breakdown).reduce((a, b) => a + b, 0),
    [breakdown],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countRes, statsRes] = await Promise.all([
        api.get('/attendance/clock-in-reviews', {
          params: statusFilter === 'all' ? {} : { status: statusFilter },
        }),
        api.get('/attendance/clock-in-reviews/count'),
        // Iter-411j — inline analytics feeds a compact banner above
        // the review list. Keeps everything on one page.
        api.get('/attendance/gps-fallback-stats?days=7').catch(() => ({ data: null })),
      ]);
      setReviews(listRes.data?.reviews || []);
      setBreakdown(countRes.data?.breakdown || {
        outside_radius_review: 0, gps_only_review: 0, selfie_failed: 0, pending: 0,
      });
      setFallbackStats(statsRes.data || null);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load clock-in reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const buildSelfieUrl = (u) => {
    if (!u) return null;
    if (u.startsWith('http')) return u;
    return `${API}${u}${u.includes('?') ? '&' : '?'}auth=${encodeURIComponent(TOKEN() || '')}`;
  };

  const toggleSelected = (bookingId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(bookingId) ? next.delete(bookingId) : next.add(bookingId);
      return next;
    });
  };

  const eligibleForBulk = useMemo(
    () => reviews.filter((r) =>
      ['outside_radius_review', 'gps_warning'].includes(r.verification),
    ),
    [reviews],
  );

  const toggleAllEligible = () => {
    setSelected((prev) => {
      const ids = new Set(eligibleForBulk.map((r) => r.booking_id));
      // If everything eligible is already selected, clear; else select all eligible.
      const allSelected = eligibleForBulk.every((r) => prev.has(r.booking_id));
      return allSelected ? new Set() : ids;
    });
  };

  const decide = async () => {
    if (!actionDialog) return;
    setSubmitting(true);
    try {
      if (actionDialog.mode === 'bulk') {
        const ids = Array.from(selected);
        const res = await api.post('/attendance/clock-in-reviews/bulk-approve', {
          booking_ids: ids,
          reason: reason.trim() || null,
        });
        const skipped = res.data?.skipped || 0;
        toast.success(
          skipped > 0
            ? `Approved ${res.data.approved} records (${skipped} skipped — only outside-radius can bulk-approve)`
            : `Approved ${res.data.approved} records`,
        );
      } else {
        const { mode, review } = actionDialog;
        await api.post(`/attendance/clock-in-reviews/${review.booking_id}/decide`, {
          decision: mode,
          reason: reason.trim() || null,
        });
        toast.success(mode === 'approve' ? 'Check-in approved' : 'Check-in rejected');
      }
      setActionDialog(null);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record decision');
    } finally {
      setSubmitting(false);
    }
  };

  const renderRow = (r) => {
    const meta = STATUS_META[r.verification] || STATUS_META.pending;
    const Icon = meta.icon;
    const talentName = `${r.talent.first_name || ''} ${r.talent.last_name || ''}`.trim() || r.talent.email || 'Unknown';
    const mapsUrl = r.shift.venue_lat && r.shift.venue_lng
      ? `https://www.google.com/maps?q=${r.shift.venue_lat},${r.shift.venue_lng}`
      : null;
    const isEligibleBulk = ['outside_radius_review', 'gps_warning'].includes(r.verification);

    return (
      <Card key={r.checkin_id} className="border-gray-200 hover:border-amber-300 transition-colors" data-testid={`review-card-${r.booking_id}`}>
        <CardContent className="p-3 sm:p-4 space-y-3">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {isEligibleBulk && (
                <Checkbox
                  checked={selected.has(r.booking_id)}
                  onCheckedChange={() => toggleSelected(r.booking_id)}
                  className="mt-1.5"
                  data-testid={`select-${r.booking_id}`}
                />
              )}
              {r.talent.photo_url ? (
                <img src={r.talent.photo_url} alt={talentName}
                     className="w-11 h-11 rounded-full object-cover border border-gray-200 shrink-0" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-gray-600">
                    {(r.talent.first_name || r.talent.email || '?')[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate text-sm sm:text-base" data-testid={`review-talent-name-${r.booking_id}`}>
                  {talentName}
                </p>
                <p className="text-xs text-gray-500 truncate">{r.talent.email}</p>
              </div>
            </div>
            <Badge className={`${meta.chip} border text-xs whitespace-nowrap shrink-0`} data-testid={`review-status-${r.booking_id}`}>
              <Icon className="w-3 h-3 mr-1" />
              {meta.label}
            </Badge>
          </div>

          {/* Reason banner */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900" data-testid={`review-reason-${r.booking_id}`}>
              {FAILURE_REASON(r)}
            </p>
          </div>

          {/* Iter-411j — GPS-fallback diagnostic strip. Shows admin the
              exact browser, device UA, method used, and any event code
              entered by the talent. Only rendered when the record is a
              gps_fallback_review. */}
          {r.verification === 'gps_fallback_review' && (r.device_info || r.gps_fallback_event_code) && (
            <div className="rounded-md bg-cyan-50 border border-cyan-200 p-2.5 text-xs text-cyan-900 space-y-1" data-testid={`gps-fallback-strip-${r.booking_id}`}>
              {r.gps_fallback_event_code && (
                <div><span className="font-semibold">Event code entered:</span> <code className="bg-white/60 px-1.5 py-0.5 rounded">{r.gps_fallback_event_code}</code></div>
              )}
              {r.gps_fallback_method && (
                <div><span className="font-semibold">Method:</span> {r.gps_fallback_method}</div>
              )}
              {r.device_info && (
                <div className="truncate" title={r.device_info}><span className="font-semibold">Device:</span> {r.device_info}</div>
              )}
            </div>
          )}

          {/* Metadata grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
            <div className="flex items-center gap-1.5 text-gray-700">
              <Clock className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              <span className="truncate">
                Clocked in: {r.checkin_timestamp ? formatDateTime12h(r.checkin_timestamp) : '—'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-700">
              <MapPin className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              <span className="truncate">
                {r.shift.venue_name || 'Venue'}
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="ml-1 text-blue-600 hover:underline inline-flex items-center">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </span>
            </div>
          </div>

          {/* Selfie (lightbox-preview) */}
          {r.selfie_url && (
            <button
              type="button"
              onClick={() => setLightbox(buildSelfieUrl(r.selfie_url))}
              className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
              data-testid={`review-selfie-${r.booking_id}`}
            >
              <Camera className="w-3.5 h-3.5" />
              View clock-in selfie
            </button>
          )}

          {/* Actions — bigger tap targets */}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              onClick={() => { setActionDialog({ mode: 'approve', review: r }); setReason(''); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
              data-testid={`approve-btn-${r.booking_id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setActionDialog({ mode: 'reject', review: r }); setReason(''); }}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 h-9"
              data-testid={`reject-btn-${r.booking_id}`}
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Link
              to={`/admin/shifts/${r.shift_id}`}
              className="inline-flex items-center text-xs text-blue-600 hover:underline self-center ml-auto"
              data-testid={`view-shift-btn-${r.booking_id}`}
            >
              Open shift <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-clock-in-reviews-page">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="-ml-2 text-gray-600" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-600" />
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">Clock-In Reviews</h1>
              <Badge className="bg-amber-100 text-amber-900 border border-amber-200" data-testid="review-total-count">
                {totalPending}
              </Badge>
            </div>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Talent who clocked in but failed GPS or selfie verification. Approve, reject, or
              bulk-approve outside-radius records — talent are never auto-blocked.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn" className="h-9">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Iter-411j — GPS Fallback analytics banner (inline, not a
            new page). Only rendered when we have at least one fallback
            in the last 7 days. Shows top reasons + top device + a
            7-day sparkline so ops can spot patterns without leaving
            the review page. */}
        {fallbackStats && fallbackStats.total_fallbacks > 0 && (
          <div className="mb-4 rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-4" data-testid="gps-fallback-banner">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-cyan-800 font-semibold">
                  <MapPin className="w-3.5 h-3.5" />
                  GPS Fallback — last {fallbackStats.window_days} days
                </div>
                <div className="mt-0.5 text-2xl font-bold text-cyan-900" data-testid="gps-fallback-total">
                  {fallbackStats.total_fallbacks}
                  <span className="text-sm font-normal text-cyan-700 ml-2">check-ins recovered from GPS failure</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStatusFilter('gps_fallback_review')}
                className="text-xs font-medium text-cyan-700 hover:text-cyan-900 underline"
                data-testid="gps-fallback-filter-btn"
              >
                Filter list → GPS Fallback
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <div className="text-[11px] uppercase text-slate-500 font-medium mb-1">By reason</div>
                <div className="space-y-1">
                  {Object.entries(fallbackStats.by_reason || {}).map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">
                        {reason === 'permission_denied' ? 'Permission denied'
                          : reason === 'position_unavailable' ? 'Device says unavailable'
                          : reason === 'timeout' ? 'Timeout'
                          : reason === 'unspecified' ? 'Unspecified'
                          : reason}
                      </span>
                      <span className="text-slate-900 font-semibold" data-testid={`gps-fallback-reason-${reason}`}>{count}</span>
                    </div>
                  ))}
                  {Object.keys(fallbackStats.by_reason || {}).length === 0 && (
                    <div className="text-xs text-slate-400">no data yet</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500 font-medium mb-1">Top devices / browsers</div>
                <div className="space-y-1">
                  {(fallbackStats.by_device || []).slice(0, 4).map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs gap-2">
                      <span className="text-slate-700 truncate max-w-[220px]" title={d.device}>{d.device || 'unknown'}</span>
                      <span className="text-slate-900 font-semibold">{d.count}</span>
                    </div>
                  ))}
                  {(fallbackStats.by_device || []).length === 0 && (
                    <div className="text-xs text-slate-400">no device data yet</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500 font-medium mb-1">Daily trend</div>
                <div className="flex items-end gap-1 h-14">
                  {(fallbackStats.by_day || []).map((d, i) => {
                    const max = Math.max(1, ...(fallbackStats.by_day || []).map((x) => x.count));
                    const h = Math.max(4, (d.count / max) * 56);
                    return (
                      <div
                        key={i}
                        title={`${d.date}: ${d.count}`}
                        className="flex-1 bg-cyan-400 rounded-t hover:bg-cyan-500 transition-colors"
                        style={{ height: `${h}px` }}
                      />
                    );
                  })}
                  {(fallbackStats.by_day || []).length === 0 && (
                    <div className="text-xs text-slate-400 self-center">no data yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter chips with per-bucket counts */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="status-chips">
          {[
            ['all', 'All', totalPending, null],
            ['outside_radius_review', 'Outside Radius', breakdown.outside_radius_review, MapPin],
            ['gps_only_review',       'Selfie Failed (GPS)', breakdown.gps_only_review, Camera],
            ['selfie_failed',         'Selfie Failed (No GPS)', breakdown.selfie_failed, Camera],
            ['gps_fallback_review',   'GPS Fallback', breakdown.gps_fallback_review || 0, MapPin],
            ['pending',               'Pending', breakdown.pending, AlertTriangle],
            ['pending_correction',    'Awaiting Correction', breakdown.pending_correction, Camera],
            ['approved_gps_only',     'Approved (GPS-only)', breakdown.approved_gps_only, Shield],
          ].map(([key, label, count, Icon]) => {
            const meta = STATUS_META[key];
            const active = statusFilter === key;
            const baseClass = active
              ? meta?.chipActive || 'bg-cyan-600 text-white border-cyan-600'
              : meta?.chip || 'bg-gray-100 text-gray-700 border-gray-200';
            return (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap transition-colors ${baseClass}`}
                data-testid={`chip-${key}`}
              >
                {Icon && <Icon className="w-3 h-3" />}
                {label}
                <span className="bg-white/40 text-current rounded-full px-1.5 py-0 text-[10px] font-semibold">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Bulk action bar — only when something selected */}
        {selected.size > 0 && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4 flex flex-wrap items-center justify-between gap-2 sticky top-0 z-10" data-testid="bulk-action-bar">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <Users className="w-4 h-4" />
              {selected.size} selected
              <button
                type="button"
                onClick={toggleAllEligible}
                className="text-xs text-amber-700 underline hover:text-amber-900 ml-2"
                data-testid="select-all-eligible"
              >
                {eligibleForBulk.every((r) => selected.has(r.booking_id)) ? 'Deselect all' : 'Select all eligible'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => { setActionDialog({ mode: 'bulk' }); setReason(''); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="bulk-approve-btn"
              >
                <Zap className="w-4 h-4 mr-1" />
                Bulk approve {selected.size}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <Card><CardContent className="p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </CardContent></Card>
        ) : reviews.length === 0 ? (
          <Card data-testid="empty-state"><CardContent className="p-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-base font-semibold text-gray-900">All clear</p>
            <p className="text-sm text-gray-600 mt-1">No clock-in reviews need attention right now.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="review-list">
            {reviews.map(renderRow)}
          </div>
        )}
      </div>

      {/* Selfie lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none">
          <DialogTitle className="sr-only">Clock-in selfie</DialogTitle>
          <DialogDescription className="sr-only">Enlarged selfie preview for moderation review.</DialogDescription>
          {lightbox && (
            <img
              src={lightbox}
              alt="Clock-in selfie"
              className="w-full max-h-[85vh] object-contain rounded"
              data-testid="selfie-lightbox-img"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.mode === 'bulk'
                ? `Bulk approve ${selected.size} check-ins`
                : actionDialog?.mode === 'approve'
                  ? 'Approve check-in'
                  : 'Reject check-in'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.mode === 'bulk'
                ? 'All selected outside-radius records will be approved. Other statuses will be skipped.'
                : actionDialog?.mode === 'approve'
                  ? 'The talent will be credited for this clock-in. Optionally add a note for the audit log.'
                  : 'The clock-in will be cleared. Talent must re-clock in. Add a reason — it goes in the audit log.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={actionDialog?.mode === 'reject'
              ? 'Reason (required by audit policy)'
              : 'Optional note (e.g., venue larger than radius)'}
            data-testid="decision-reason-input"
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={submitting} data-testid="decision-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={decide}
              disabled={submitting || (actionDialog?.mode === 'reject' && !reason.trim())}
              className={actionDialog?.mode === 'reject'
                ? 'bg-rose-600 hover:bg-rose-700 text-white'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
              data-testid="decision-submit-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {actionDialog?.mode === 'reject' ? 'Reject' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClockInReviews;
